import { useState } from 'react'
import {
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    DocumentChartBarIcon,
    ArrowDownTrayIcon,
    ClockIcon,
} from '@heroicons/react/24/outline'
import { normalizeRelatorioStatus, isRelatorioEmAndamento, relatorioStatusStyle } from '../../../utils/relatorioStatus'

const formatDateTime = (value) => {
    if (!value) return '—'
    const d = new Date(String(value).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const RelatoriosTable = ({
    data = [],
    loading,
    page,
    recordsPerPage,
    previous,
    next,
    onLoadObras,
    onDownload,
    obrasCache = {},
    obrasLoading = {},
}) => {
    const [expanded, setExpanded] = useState({})
    const [downloadingId, setDownloadingId] = useState(null)

    const toggleExpand = async (item) => {
        const isOpen = expanded[item.id]
        if (isOpen) {
            setExpanded((prev) => ({ ...prev, [item.id]: false }))
            return
        }
        setExpanded((prev) => ({ ...prev, [item.id]: true }))
        if (!obrasCache[item.id]) {
            await onLoadObras?.(item.id)
        }
    }

    const handleDownload = async (obraRelatorio) => {
        setDownloadingId(obraRelatorio.id)
        await onDownload?.(obraRelatorio.id)
        setDownloadingId(null)
    }

    return (
        <>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                    <div className="p-1.5 bg-gray-900 rounded-lg">
                        <DocumentChartBarIcon className="w-4 h-4 text-brand-yellow" />
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900">Processamentos de relatório</h2>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <span className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin mb-3" />
                        <p className="text-sm text-gray-400">Carregando relatórios...</p>
                    </div>
                ) : data.length > 0 ? (
                    <div className="divide-y divide-gray-50">
                        {data.map((item) => {
                            const isOpen = expanded[item.id]
                            const obras = obrasCache[item.id] ?? []
                            const loadingObras = obrasLoading[item.id]
                            const statusLabel = normalizeRelatorioStatus(item.status)
                            const emAndamento = isRelatorioEmAndamento(item.status)
                            const progress = item.quantidade_total > 0
                                ? Math.round((item.quantidade_processada / item.quantidade_total) * 100)
                                : 0

                            return (
                                <div key={item.id}>
                                    <div className="px-4 py-4 hover:bg-gray-50/80 transition-colors">
                                        <div className="flex items-start gap-3">
                                            <button
                                                type="button"
                                                onClick={() => toggleExpand(item)}
                                                className="mt-0.5 p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                                                title={isOpen ? 'Recolher obras' : 'Ver obras'}
                                            >
                                                {isOpen
                                                    ? <ChevronDownIcon className="w-4 h-4" />
                                                    : <ChevronRightIcon className="w-4 h-4" />}
                                            </button>

                                            <div className="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-4 gap-3">
                                                <div>
                                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Status</p>
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${relatorioStatusStyle(item.status)}`}>
                                                        {statusLabel}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Progresso</p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {item.quantidade_processada ?? 0} / {item.quantidade_total ?? 0}
                                                        <span className="text-gray-400 ml-1">({progress}%)</span>
                                                    </p>
                                                </div>
                                                <div className="lg:col-span-2">
                                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Descrição</p>
                                                    <p className="text-sm text-gray-700 truncate" title={item.descricao}>{item.descricao || '—'}</p>
                                                </div>
                                            </div>

                                            <div className="text-right flex-shrink-0">
                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Criado em</p>
                                                <p className="text-xs text-gray-500 flex items-center justify-end gap-1">
                                                    <ClockIcon className="w-3.5 h-3.5" />
                                                    {formatDateTime(item.data_criacao)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {isOpen && (
                                        <div className="bg-gray-50/70 border-t border-gray-100 px-4 py-4">
                                            {loadingObras ? (
                                                <div className="flex items-center justify-center py-8">
                                                    <span className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin" />
                                                </div>
                                            ) : obras.length === 0 ? (
                                                <div className="text-center py-6 px-4">
                                                    {emAndamento || (item.quantidade_total > 0 && progress < 100) ? (
                                                        <>
                                                            <p className="text-sm font-medium text-amber-700">Relatório em processamento</p>
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                As obras deste processamento ficarão visíveis aqui conforme forem concluídas.
                                                                {item.descricao ? ` ${item.descricao}` : ''}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <p className="text-sm text-gray-400">Nenhuma obra vinculada a este processamento.</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 px-1">Obras do processamento</p>
                                                    {obras.map((obra) => {
                                                        const obraStatus = normalizeRelatorioStatus(obra.status)
                                                        return (
                                                            <div
                                                                key={obra.id}
                                                                className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
                                                            >
                                                                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-2">
                                                                    <div>
                                                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Obra</p>
                                                                        <p className="text-sm font-medium text-gray-900 truncate">{obra.nome_obra}</p>
                                                                        <p className="text-xs text-gray-400">{obra.codigo_obra}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Empreendimento</p>
                                                                        <p className="text-sm text-gray-700 truncate">{obra.nome_empreendimento}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Período</p>
                                                                        <p className="text-sm text-gray-700">{obra.data_inicial} — {obra.data_final}</p>
                                                                    </div>
                                                                </div>

                                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${relatorioStatusStyle(obra.status)}`}>
                                                                        {obraStatus}
                                                                    </span>
                                                                    {obra.etapa && (
                                                                        <span className="text-xs text-gray-500 hidden sm:inline">{obra.etapa}</span>
                                                                    )}
                                                                    {obra.statusDowload && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDownload(obra)}
                                                                            disabled={downloadingId === obra.id}
                                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark transition-colors disabled:opacity-50"
                                                                        >
                                                                            {downloadingId === obra.id ? (
                                                                                <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-900/20 border-t-gray-900 animate-spin" />
                                                                            ) : (
                                                                                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                                                                            )}
                                                                            Download
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center mb-3">
                            <DocumentChartBarIcon className="w-6 h-6 text-brand-yellow" />
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">Nenhum relatório encontrado</h3>
                        <p className="text-xs text-gray-400">Gere um novo relatório para acompanhar o processamento.</p>
                    </div>
                )}
            </div>

            {data.length > 0 && (
                <div className="flex items-center justify-between bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm mt-4">
                    <span className="text-sm text-gray-500">Página {(page ?? 0) + 1} · {data.length} registros</span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={previous}
                            disabled={page === 0}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeftIcon className="w-4 h-4" /> Anterior
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            disabled={data.length < (recordsPerPage ?? 10)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            Próximo <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

export default RelatoriosTable
